import { useEffect, useState, useTransition } from "react";
import { Link } from "react-router-dom";
import { Copy, Check, Send, UploadCloud } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input, Select, Label } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { useDict, useLocale } from "@/lib/i18n/context";
import { LOCALES, LOCALE_LABELS } from "@/lib/i18n/config";
import { resolveApiError } from "@/lib/api-error";

type JobOption = { id: string; title: string };
type ClientOption = { id: string; client_company_name: string };

type InviteResult = {
  error?: string;
  code?: string;
  interviewId?: string;
  link?: string;
  emailed?: boolean;
};

/** Netlify CLI proxies request bodies with a ~6 MiB cap; stay under it. */
const MAX_CV_BYTES = 5 * 1024 * 1024;

async function getCandidateHistory(email: string) {
  const clean = email.trim();
  if (!clean || !clean.includes("@")) return [];
  const res = await fetch(
    `/api/candidates/history?email=${encodeURIComponent(clean)}`,
    { credentials: "include" },
  );
  if (!res.ok) return [];
  const data = (await res.json()) as { history?: unknown[] };
  return data.history ?? [];
}

export function InviteForm({
  jobs,
  defaultJobId,
  defaultName,
  defaultEmail,
}: {
  jobs: JobOption[];
  defaultJobId?: string;
  defaultName?: string;
  defaultEmail?: string;
}) {
  const t = useDict();
  const { locale } = useLocale();
  const [pending, startTransition] = useTransition();
  const [result, setResult] = useState<InviteResult | null>(null);
  const [copied, setCopied] = useState(false);
  const [fileName, setFileName] = useState<string>("");
  const [clients, setClients] = useState<ClientOption[]>([]);

  useEffect(() => {
    fetch("/api/partnerships", { credentials: "include" })
      .then(async (res) => {
        if (!res.ok) return null;
        return res.json() as Promise<{
          activeAsAgency?: ClientOption[];
        }>;
      })
      .then((data) => {
        if (data?.activeAsAgency) setClients(data.activeAsAgency);
      })
      .catch(() => {});
  }, []);

  function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    const cv = fd.get("cv");
    const cvSize =
      cv instanceof File && cv.size > 0 ? cv.size : 0;
    // #region agent log
    fetch("http://127.0.0.1:7892/ingest/b0e9c3ac-9fe9-40e4-b61a-a0051cd95f6a", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Debug-Session-Id": "629ce6",
      },
      body: JSON.stringify({
        sessionId: "629ce6",
        runId: "invite-white-post",
        hypothesisId: "A",
        location: "invite-form.tsx:onSubmit:start",
        message: "invite submit start",
        data: {
          cvSize,
          cvOver6mb: cvSize > 6e6,
          blockedClientSide: cvSize > MAX_CV_BYTES,
          hasPartnershipField: fd.has("partnership_id"),
        },
        timestamp: Date.now(),
      }),
    }).catch(() => {});
    // #endregion
    if (cvSize > MAX_CV_BYTES) {
      setResult({ error: t.errors.cvTooLarge });
      return;
    }
    startTransition(async () => {
      try {
        const res = await fetch("/api/candidates/invite", {
          method: "POST",
          credentials: "include",
          body: fd,
        });
        const text = await res.text();
        let data: InviteResult;
        try {
          data = JSON.parse(text) as InviteResult;
        } catch {
          // #region agent log
          fetch("http://127.0.0.1:7892/ingest/b0e9c3ac-9fe9-40e4-b61a-a0051cd95f6a", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-Debug-Session-Id": "629ce6",
            },
            body: JSON.stringify({
              sessionId: "629ce6",
              runId: "invite-white-post",
              hypothesisId: "D",
              location: "invite-form.tsx:onSubmit:nonjson",
              message: "invite response not JSON",
              data: {
                status: res.status,
                bodyPreview: text.slice(0, 200),
                cvSize,
              },
              timestamp: Date.now(),
            }),
          }).catch(() => {});
          // #endregion
          setResult({ error: t.errors.interviewCreateFailed });
          return;
        }
        // #region agent log
        fetch("http://127.0.0.1:7892/ingest/b0e9c3ac-9fe9-40e4-b61a-a0051cd95f6a", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Debug-Session-Id": "629ce6",
          },
          body: JSON.stringify({
            sessionId: "629ce6",
            runId: "invite-white-post",
            hypothesisId: "D",
            location: "invite-form.tsx:onSubmit:ok",
            message: "invite response parsed",
            data: {
              status: res.status,
              hasLink: Boolean(data.link),
              hasError: Boolean(data.error),
              emailed: data.emailed ?? null,
              cvSize,
            },
            timestamp: Date.now(),
          }),
        }).catch(() => {});
        // #endregion
        if (data.error === "ALREADY_INTERVIEWED") {
          const email = String(fd.get("email") || "");
          await getCandidateHistory(email);
          const ok = window.confirm(
            "This email was already invited. Invite again?",
          );
          if (!ok) {
            setResult({ error: t.errors.generic });
            return;
          }
          fd.set("confirm_reinvite", "1");
          const retry = await fetch("/api/candidates/invite", {
            method: "POST",
            credentials: "include",
            body: fd,
          });
          const retryData = (await retry.json()) as InviteResult;
          if (retryData.error && retryData.error !== "ALREADY_INTERVIEWED") {
            setResult({
              error: resolveApiError(t, retryData, "interviewCreateFailed"),
            });
            return;
          }
          setResult(retryData);
          return;
        }
        if (data.error) {
          setResult({
            error: resolveApiError(t, data, "interviewCreateFailed"),
          });
          return;
        }
        setResult(data);
      } catch (err) {
        // #region agent log
        fetch("http://127.0.0.1:7892/ingest/b0e9c3ac-9fe9-40e4-b61a-a0051cd95f6a", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Debug-Session-Id": "629ce6",
          },
          body: JSON.stringify({
            sessionId: "629ce6",
            runId: "invite-white-post",
            hypothesisId: "A",
            location: "invite-form.tsx:onSubmit:catch",
            message: "invite fetch threw",
            data: {
              err: String((err as Error)?.message || err).slice(0, 200),
              cvSize,
            },
            timestamp: Date.now(),
          }),
        }).catch(() => {});
        // #endregion
        setResult({ error: t.errors.networkError });
      }
    });
  }

  if (result && !result.error && result.link) {
    // #region agent log
    fetch("http://127.0.0.1:7892/ingest/b0e9c3ac-9fe9-40e4-b61a-a0051cd95f6a", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Debug-Session-Id": "629ce6",
      },
      body: JSON.stringify({
        sessionId: "629ce6",
        runId: "invite-white",
        hypothesisId: "B",
        location: "invite-form.tsx:success-render",
        message: "rendering invite success UI",
        data: { interviewId: result.interviewId ?? null },
        timestamp: Date.now(),
      }),
    }).catch(() => {});
    // #endregion
    return (
      <Card className="animate-fade-in">
        <CardContent className="space-y-4 pt-6 text-center">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
            <Check className="h-7 w-7" />
          </div>
          <h2 className="text-xl font-bold">{t.candidates.invitedTitle}</h2>
          <p className="text-sm text-muted">
            {result.emailed
              ? t.candidates.emailedMsg
              : t.candidates.notEmailedMsg}
          </p>
          <div className="flex items-center gap-2 rounded-xl border border-border bg-surface p-2">
            <span className="flex-1 truncate px-2 text-sm">{result.link}</span>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                navigator.clipboard.writeText(result.link!);
                setCopied(true);
                setTimeout(() => setCopied(false), 1500);
              }}
            >
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            </Button>
          </div>
          <div className="flex justify-center gap-3 pt-2">
            <Link to={`/interviews/${result.interviewId}`}>
              <Button>{t.candidates.viewInterview}</Button>
            </Link>
            <Button variant="outline" onClick={() => setResult(null)}>
              {t.candidates.inviteAnother}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="pt-6">
        <form onSubmit={onSubmit} className="space-y-4">
          {clients.length > 0 && (
            <div>
              <Label>{t.partners.workingFor}</Label>
              <Select name="partnership_id" defaultValue="">
                <option value="">{t.partners.ownCompany}</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.client_company_name}
                  </option>
                ))}
              </Select>
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label>{t.candidates.candidateName} *</Label>
              <Input
                name="name"
                required
                defaultValue={defaultName || ""}
                placeholder={t.candidates.candidateNamePh}
              />
            </div>
            <div>
              <Label>{t.candidates.candidateEmail} *</Label>
              <Input
                name="email"
                type="email"
                required
                defaultValue={defaultEmail || ""}
                placeholder={t.candidates.candidateEmailPh}
              />
            </div>
          </div>

          <div>
            <Label>{t.candidates.jobOptional}</Label>
            <Select name="job_id" defaultValue={defaultJobId ?? ""}>
              <option value="">{t.candidates.generalOption}</option>
              {jobs.map((j) => (
                <option key={j.id} value={j.id}>
                  {j.title}
                </option>
              ))}
            </Select>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label>{t.candidates.interviewLanguage}</Label>
              <Select name="language" defaultValue={locale}>
                {LOCALES.map((l) => (
                  <option key={l} value={l}>
                    {LOCALE_LABELS[l]}
                  </option>
                ))}
              </Select>
            </div>
            <div>
              <Label>{t.candidates.interviewTime}</Label>
              <Input name="scheduled_at" type="datetime-local" />
            </div>
          </div>

          <div>
            <Label>{t.candidates.cvLabel}</Label>
            <label className="flex cursor-pointer items-center gap-3 rounded-xl border border-dashed border-border bg-surface px-4 py-4 text-sm text-muted hover:bg-surface-hover">
              <UploadCloud className="h-5 w-5" />
              <span>{fileName || t.candidates.cvUpload}</span>
              <input
                type="file"
                name="cv"
                accept="application/pdf"
                className="hidden"
                onChange={(e) => setFileName(e.target.files?.[0]?.name ?? "")}
              />
            </label>
          </div>

          {result?.error && (
            <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
              {result.error}
            </p>
          )}

          <Button type="submit" className="w-full" loading={pending}>
            <Send className="h-4 w-4" /> {t.candidates.sendInvitation}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
